import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/contestants");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid md:grid-cols-2">
      <div className="hidden md:flex relative bg-black p-12 items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=1200&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover grayscale opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/60 to-transparent" />
        <div className="relative z-10">
          <Logo className="h-12 w-auto mb-8" />
          <h2
            className="text-4xl md:text-5xl font-black tracking-tighter leading-[1]"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Welcome back to <span className="text-[#2BA84A]">Agrovest</span>.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-5">
          <div className="text-xs uppercase tracking-[0.3em] text-[#F26A1F]">Login</div>
          <h1
            className="text-4xl font-black tracking-tighter leading-[1]"
            style={{ fontFamily: "'Cabinet Grotesk', 'Bricolage Grotesque', sans-serif" }}
          >
            Sign in to vote
          </h1>
          <p className="text-white/60 text-sm">
            New here?{" "}
            <Link to="/register" data-testid={LOGIN.registerLink} className="text-[#F26A1F] font-bold underline">Create an account</Link>.
          </p>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={LOGIN.emailInput}
              className="w-full h-12 px-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={LOGIN.passwordInput}
              className="w-full h-12 px-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid={LOGIN.submitButton}
            className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] disabled:opacity-60 text-white text-xs uppercase tracking-[0.2em] font-bold transition-colors"
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
